import { formatCurrency, formatNumber, resolveTokenCurrency } from '../../utils/formatUtils';
import {
  getAggregationNumericResult,
  formatAggregatedDisplayForReport,
  getAggregatableNumericForExport,
  parseReportSortNumeric,
  reportFieldIsCurrencyLike,
  reportFieldShowsDefaultSum,
  unwrapReportScalar,
} from '../../utils/reportCurrencyAggregate';

export function isFetchRecordsCell(cell) {
  return (
    cell != null &&
    typeof cell === 'object' &&
    !Array.isArray(cell) &&
    ('value' in cell || 'type' in cell || 'displayValue' in cell)
  );
}

export function buildAiChatFieldMeta(col, rows, objectName, columnMeta) {
  if (columnMeta?.[col]) {
    const meta = columnMeta[col];
    return {
      field_name: col,
      data_type: meta.data_type || meta.dataType,
      scale: meta.scale,
      object_name: meta.object_name || objectName || 'main',
      display_config: meta.display_config ?? meta.displayConfig,
    };
  }
  const sampleRow = rows.find((r) => r?.[col] != null);
  const sample = sampleRow?.[col];
  if (isFetchRecordsCell(sample)) {
    const type = String(sample.type || sample.data_type || '').toLowerCase();
    return {
      field_name: col,
      data_type: type,
      scale: sample.scale,
      object_name: objectName || 'main',
      display_config: sample.displayConfig ?? sample.display_config,
    };
  }
  const lower = String(col || '').toLowerCase();
  let dataType = 'text';
  if (
    /(?:^|_)(total|sum|avg|average|min|max)_(?:amount|revenue|price)/.test(lower) ||
    /(?:^|_)(amount|revenue|price|total|cost|balance)(?:_|$)/.test(lower)
  ) {
    dataType = 'currency';
  } else if (/(?:^|_)count(?:_|$)/.test(lower) || /^(count|qty|quantity)$/.test(lower)) {
    dataType = 'number';
  }
  return {
    field_name: col,
    data_type: dataType,
    object_name: objectName || 'main',
  };
}

function isPercentLikeColumn(col, columnLabels) {
  const label = `${columnLabels?.[col] || ''} ${col}`.toLowerCase();
  return /(?:%|percent|percentage|win\s*rate|win_rate|winrate)/.test(label);
}

export function isFetchRecordsRow(row) {
  if (!row || typeof row !== 'object') return false;
  return Object.values(row).some((cell) => isFetchRecordsCell(cell));
}

/** Flat SQL rows (GROUP BY / SUM) — no per-record id, not fetchRecords-shaped. */
export function rowsLookLikeSqlAggregate(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  if (rows.some(isFetchRecordsRow)) return false;
  return !rows.some((r) => {
    const id = r?.id?.value ?? r?.id;
    return id != null && String(id).trim() !== '';
  });
}

function columnLooksLikeAggregateAmount(col, columnLabels) {
  const label = `${columnLabels?.[col] || ''} ${col}`.toLowerCase();
  if (/(?:%|percent|percentage|win\s*rate|win_rate)/.test(label)) return false;
  if (/(total|sum|aggregate|grand\s*total)/.test(label)) return true;
  const colLower = String(col || '').toLowerCase();
  return /(?:^|_)(total|sum)(?:_|$)/.test(colLower);
}

/**
 * Format a flat SQL aggregate currency cell in base currency (owner-wise totals, etc.).
 * Skips fetchRecords detail rows — those keep per-record currency display.
 */
function parseAggregateCellNumeric(value) {
  const raw = unwrapReportScalar(value);
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return parseReportSortNumeric(raw);
}

function looksLikePreformattedCurrency(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || /^-?\d/.test(trimmed)) return false;
  return parseAggregateCellNumeric(trimmed) != null;
}

function formatAiChatAggregateCurrencyCell(col, value, { currencyCtx, objectName, rows, columnMeta }) {
  if (looksLikePreformattedCurrency(value)) {
    return String(value).trim();
  }
  const meta = buildAiChatFieldMeta(col, rows || [{}], objectName, columnMeta);
  if (!reportFieldIsCurrencyLike(meta)) return null;
  const n = parseAggregateCellNumeric(value);
  if (!Number.isFinite(n)) return null;
  const displayCode = getAiChatAggregateDisplayCurrency(currencyCtx);
  const decimalPlaces =
    meta.scale !== undefined && meta.scale !== null && meta.scale !== ''
      ? parseInt(meta.scale, 10)
      : 2;
  return formatCurrency(n, displayCode, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

export function formatAiChatFlatAggregateCurrency(
  col,
  value,
  { columnLabels, currencyCtx, objectName, rows, dataFormat, columnMeta }
) {
  if (dataFormat === 'fetchRecords') return null;
  // Relationship aggregate + SQL currency aggregate: values in base or org currency
  if (dataFormat === 'currencyAggregate' || dataFormat === 'aggregate') {
    const formatted = formatAiChatAggregateCurrencyCell(col, value, {
      currencyCtx,
      objectName,
      rows,
      columnMeta,
    });
    if (formatted != null) return formatted;
  }
  if (rows?.length && rows.some(isFetchRecordsRow)) return null;

  const meta = buildAiChatFieldMeta(col, rows || [{}], objectName, columnMeta);
  const isCurrencyCol = reportFieldIsCurrencyLike(meta);
  const aggregateContext =
    dataFormat === 'aggregate' ||
    dataFormat === 'currencyAggregate' ||
    rowsLookLikeSqlAggregate(rows) ||
    columnLooksLikeAggregateAmount(col, columnLabels);

  if (!isCurrencyCol || !aggregateContext) return null;

  const n = parseAggregateCellNumeric(value);
  if (!Number.isFinite(n)) return null;

  const decimalPlaces =
    meta.scale !== undefined && meta.scale !== null && meta.scale !== ''
      ? parseInt(meta.scale, 10)
      : 2;
  const fracOpts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  const displayCode = getAiChatAggregateDisplayCurrency(currencyCtx);

  return formatAggregatedDisplayForReport(
    n,
    meta,
    'sum',
    getAiChatConversionBase(currencyCtx),
    (res) => formatCurrency(res, displayCode, fracOpts)
  );
}

export function columnSupportsTableTotal(col, rows, objectName, columnLabels, columnMeta) {
  if (isPercentLikeColumn(col, columnLabels)) return false;
  const meta = buildAiChatFieldMeta(col, rows, objectName, columnMeta);
  if (reportFieldIsCurrencyLike(meta) || reportFieldShowsDefaultSum(meta)) {
    return rows.some((row) => {
      const cell = row?.[col];
      const raw = isFetchRecordsCell(cell) ? unwrapReportScalar(cell) : cell;
      const n = parseAggregateCellNumeric(raw);
      return Number.isFinite(n);
    });
  }
  return false;
}

export function getAiChatConversionBase(currencyCtx) {
  if (!currencyCtx?.multiCurrencyEnabled || !currencyCtx?.baseCurrencyCode) {
    return null;
  }
  return String(currencyCtx.baseCurrencyCode).trim().toUpperCase();
}

/** Base currency when multi-currency is on; otherwise org/token currency from settings. */
export function getAiChatAggregateDisplayCurrency(currencyCtx) {
  const conversionBase = getAiChatConversionBase(currencyCtx);
  if (conversionBase) return conversionBase;
  if (!currencyCtx?.multiCurrencyEnabled) {
    return resolveTokenCurrency('INR');
  }
  // Multi-currency on but base not loaded yet — still show org currency symbol
  return resolveTokenCurrency('INR');
}

export function parseAiChatChartNumeric(row, col, rows, objectName, currencyCtx) {
  const fieldMeta = buildAiChatFieldMeta(col, rows, objectName);
  const conversionBase = getAiChatConversionBase(currencyCtx);
  if (conversionBase && reportFieldIsCurrencyLike(fieldMeta)) {
    return getAggregatableNumericForExport(
      fieldMeta,
      row,
      conversionBase,
      (field, r) => {
        const cell = r?.[field.field_name];
        return unwrapReportScalar(cell);
      },
      currencyCtx?.exchangeRateLookup
    );
  }
  const cell = row?.[col];
  const raw = isFetchRecordsCell(cell) ? unwrapReportScalar(cell) : cell;
  const n = parseAggregateCellNumeric(raw);
  return Number.isFinite(n) ? n : 0;
}

export function formatAiChatTableTotal(
  col,
  rows,
  objectName,
  columnLabels,
  currencyCtx,
  dataFormat,
  columnMeta
) {
  const fieldMeta = buildAiChatFieldMeta(col, rows, objectName, columnMeta);
  const conversionBase = getAiChatConversionBase(currencyCtx);

  if (dataFormat === 'currencyAggregate' || dataFormat === 'aggregate') {
    const total = (rows || []).reduce((sum, row) => {
      const n = parseAggregateCellNumeric(row?.[col]);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    if (!Number.isFinite(total)) return '—';
    const decimalPlaces =
      fieldMeta.scale !== undefined && fieldMeta.scale !== null && fieldMeta.scale !== ''
        ? parseInt(fieldMeta.scale, 10)
        : 2;
    const fracOpts = {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };
    if (!reportFieldIsCurrencyLike(fieldMeta)) {
      return formatNumber(total, fracOpts);
    }
    return formatCurrency(total, getAiChatAggregateDisplayCurrency(currencyCtx), fracOpts);
  }

  const total = getAggregationNumericResult(
    rows,
    fieldMeta,
    'sum',
    conversionBase,
    currencyCtx?.exchangeRateLookup,
    objectName
  );
  if (total == null || !Number.isFinite(total)) return '—';

  const decimalPlaces =
    fieldMeta.scale !== undefined && fieldMeta.scale !== null && fieldMeta.scale !== ''
      ? parseInt(fieldMeta.scale, 10)
      : 2;
  const fracOpts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  if (reportFieldIsCurrencyLike(fieldMeta) && conversionBase) {
    return formatAggregatedDisplayForReport(
      total,
      fieldMeta,
      'sum',
      conversionBase,
      (res) => formatCurrency(res, conversionBase, fracOpts)
    );
  }

  if (reportFieldIsCurrencyLike(fieldMeta)) {
    return formatCurrency(total, conversionBase || 'INR', fracOpts);
  }

  return formatNumber(total, fracOpts);
}
